export type PlanId = "free" | "plus" | "pro" | "expert";

export type PlanLimits = {
  perMonth: number | null;
  perWeek: number | null;
  perDay: number | null;
  /** Dygnsspärr för smarta sökningar. Månadstaket nedan är det som styr. */
  aiSearchPerDay: number;
  /** Antal smarta sökningar per abonnemangsperiod. */
  aiSearchPerMonth: number;
  /** Dygnsspärr för bildrensningar. */
  cleanImagesPerDay: number;
  /** Antal bildrensningar per abonnemangsperiod. */
  cleanImagesPerMonth: number;
  /** Antal lyft per månad. Tillsammans med dygn per lyft ger det månadens lyftdagar. */
  promotionsPerMonth: number;
  /** Minsta längd på ett lyft i dygn, och steget lyftlängden väljs i. */
  promotionDays: number;
  /** Antal AI-omskrivningar av annonstext per månad (null = obegränsat). */
  aiRewritesPerMonth: number | null;
  /** Max antal bilder per annons. */
  maxImages: number;
  /** Om man får ladda upp video till annonsen. */
  video: boolean;
  /** Om man får fotoskanna: ladda upp många bilder och låta AI:n gruppera dem. */
  photoScan: boolean;
  /** Antal djupvärderingar per månad (null = obegränsat inom rimlig användning). */
  deepValuationsPerMonth: number | null;
};


/** En punkt i funktionslistan. Är den märkt med fairUse visas en asterisk som pekar på fotnoten. */
export type PlanFeature = {
  text: string;
  /** Punkten omfattas av rimlig användning och får en asterisk. */
  fairUse?: boolean;
};

export type PlanDefinition = {
  id: PlanId;
  name: string;
  priceLabel: string;
  priceSek: number;
  /** Pris vid årsbetalning (10 månader – två månader på köpet). */
  priceYearSek: number;
  priceYearLabel: string;
  tagline: string;
  limits: PlanLimits;
  features: PlanFeature[];
  /** Exakta gränser, samlade under funktionslistan i stället för i varje rad. */
  notes: string[];
};

/** Asteriskens text. Samma för alla planer. */
export const FAIR_USE_NOTE =
  "Obegränsat inom rimlig användning. Gränserna per dygn och vecka finns för att tjänsten ska fungera bra för alla och användas som den är tänkt.";

/**
 * Gäller alla planer: priser och värderingar vi föreslår är uppskattningar.
 * Visas som en egen rad under varje plan.
 */
export const PLAN_DISCLAIMER =
  "Priser och värderingar vi föreslår, i annonser och i skanningar, är uppskattningar och ingen värdering. Du bestämmer alltid text och pris själv.";


export const PLANS: Record<PlanId, PlanDefinition> = {
  free: {
    id: "free",
    name: "Gratis",
    priceLabel: "0 kr",
    priceSek: 0,
    priceYearSek: 0,
    priceYearLabel: "0 kr",
    tagline: "Perfekt för dig som rensar ut några saker.",
    limits: {
      perMonth: 10,
      perWeek: null,
      perDay: 3,
      aiSearchPerDay: 10,
      aiSearchPerMonth: 100,
      cleanImagesPerDay: 3,
      cleanImagesPerMonth: 20,
      promotionsPerMonth: 0,
      promotionDays: 0,
      aiRewritesPerMonth: 5,
      maxImages: 3,
      video: false,
      photoScan: false,
      deepValuationsPerMonth: 0,
    },
    features: [
      { text: "10 annonser per månad" },
      { text: "Vi skriver titel, text och prisförslag" },
      { text: "Upp till 3 bilder per annons" },
      { text: "Rensa bakgrunden på en bild" },
      { text: "Karta och interna meddelanden" },
      { text: "Smart sökning bland annonserna" },
      { text: "Skriv om annonstexten med ett klick" },
    ],
    notes: [
      "3 nya annonser per dygn",
      "20 bildrensningar per månad, som mest 3 per dygn",
      "100 smarta sökningar per månad, som mest 10 per dygn",
      "5 omskrivningar av annonstext per månad",
    ],
  },
  plus: {
    id: "plus",
    name: "Plus",
    priceLabel: "59 kr/mån",
    priceSek: 59,
    priceYearSek: 590,
    priceYearLabel: "590 kr/år",
    tagline: "För dig som säljer regelbundet.",
    limits: {
      perMonth: 60,
      perWeek: null,
      perDay: 15,
      aiSearchPerDay: 100,
      aiSearchPerMonth: 1000,
      cleanImagesPerDay: 25,
      cleanImagesPerMonth: 200,
      promotionsPerMonth: 5,
      promotionDays: 3,
      aiRewritesPerMonth: 40,
      maxImages: 6,
      video: false,
      photoScan: true,
      deepValuationsPerMonth: 0,
    },
    features: [
      { text: "Allt i Gratis" },
      { text: "60 annonser per månad" },
      { text: "Upp till 6 bilder per annons" },
      { text: "Fotoskanning: fota flera prylar på en gång", fairUse: true },
      { text: "Lyft fram dina annonser högst upp" },
      { text: "Pausa och återaktivera annonser fritt" },
      { text: "Rensa bakgrunden på bilder" },
    ],
    notes: [
      "15 nya annonser per dygn",
      "3 fotoskanningar per dygn, upp till 30 bilder per omgång",
      "200 bildrensningar per månad, som mest 25 per dygn",
      "15 lyftdagar per månad – ett lyft bokas i steg om 3 dygn (upp till 5 lyft)",
      "1 000 smarta sökningar per månad, som mest 100 per dygn",
      "40 omskrivningar av annonstext per månad",
    ],
  },
  pro: {
    id: "pro",
    name: "Pro",
    priceLabel: "139 kr/mån",
    priceSek: 139,
    priceYearSek: 1390,
    priceYearLabel: "1 390 kr/år",
    tagline: "För storsäljare och små företag.",
    limits: {
      perMonth: 200,
      perWeek: 100,
      perDay: 25,
      aiSearchPerDay: 400,
      aiSearchPerMonth: 4000,
      cleanImagesPerDay: 100,
      cleanImagesPerMonth: 1000,
      promotionsPerMonth: 25,
      promotionDays: 7,
      aiRewritesPerMonth: null,
      maxImages: 6,
      video: true,
      photoScan: true,
      deepValuationsPerMonth: 5,
    },
    features: [
      { text: "Allt i Plus" },
      { text: "200 annonser per månad" },
      { text: "Videoskanning: filma ett rum och få en lista på prylarna", fairUse: true },
      { text: "Fotoskanning i fler omgångar", fairUse: true },
      { text: "Video på annonsen som visar prylen i rörelse" },
      { text: "Rensa bakgrunden på bilder" },
      { text: "Djupvärdering av 5 prylar per månad, med följdfrågor och marknadsunderlag" },
      { text: "Lyft fram dina annonser högst upp" },
      { text: "Obegränsat med omskrivningar av annonstext", fairUse: true },
      { text: "Prioriterad support" },
    ],
    notes: [
      "200 annonser per månad – 25 per dygn och 100 per vecka",
      "10 videoskanningar per dygn, upp till 60 sekunder film per skanning",
      "15 fotoskanningar per dygn, upp till 30 bilder per omgång",
      "1 000 bildrensningar per månad, som mest 100 per dygn",
      "5 djupvärderingar per månad",
      "175 lyftdagar per månad – ett lyft bokas i steg om 7 dygn (upp till 25 lyft)",
      "4 000 smarta sökningar per månad, som mest 400 per dygn",
    ],
  },
  expert: {
    id: "expert",
    name: "Expert",
    priceLabel: "349 kr/mån",
    priceSek: 349,
    priceYearSek: 3490,
    priceYearLabel: "3 490 kr/år",
    tagline: "För dig som säljer konst, märkesvaror och samlarobjekt.",
    limits: {
      perMonth: 500,
      perWeek: 200,
      perDay: 40,
      aiSearchPerDay: 250,
      aiSearchPerMonth: 7500,
      cleanImagesPerDay: 150,
      cleanImagesPerMonth: 1500,
      promotionsPerMonth: 40,
      promotionDays: 7,
      aiRewritesPerMonth: null,
      maxImages: 12,
      video: true,
      photoScan: true,
      deepValuationsPerMonth: null,
    },
    features: [
      { text: "Allt i Pro" },
      { text: "500 annonser per månad" },
      { text: "Djupvärdering: vi ställer följdfrågor och tar fram riktigt marknadsunderlag", fairUse: true },
      { text: "Närbilder på signatur, etikett och serienummer i frågeflödet" },
      { text: "Källhänvisat underlag med länkar till jämförbara försäljningar" },
      { text: "Värde i tre nivåer: snabb försäljning, marknadsvärde och toppnivå" },
      { text: "Upp till 12 bilder per annons" },
      { text: "Prioriterad kö på skanningar och värderingar" },
    ],
    notes: [
      "Obegränsat med djupvärderingar inom rimlig användning",
      "500 annonser per månad – 40 per dygn och 200 per vecka",
      "1 500 bildrensningar per månad, som mest 150 per dygn",
      "280 lyftdagar per månad – ett lyft bokas i steg om 7 dygn (upp till 40 lyft)",
      "7 500 smarta sökningar per månad, som mest 250 per dygn",
    ],
  },
};

export const PLAN_ORDER: PlanId[] = ["free", "plus", "pro", "expert"];

/** Nivåernas inbördes ordning, för jämförelser som "minst Pro". */
export const PLAN_RANK: Record<PlanId, number> = { free: 0, plus: 1, pro: 2, expert: 3 };

/** Sant när planen är minst den angivna nivån. */
export function planAtLeast(plan: PlanId | null | undefined, tier: PlanId): boolean {
  if (!plan) return false;
  return PLAN_RANK[plan] >= PLAN_RANK[tier];
}


/**
 * Gränser för konton som inte ska begränsas alls (admin och superadmin).
 * Ingen kvot, alla funktioner öppna.
 */
export const UNLIMITED_LIMITS: PlanLimits = {
  perMonth: null,
  perWeek: null,
  perDay: null,
  aiSearchPerDay: Number.MAX_SAFE_INTEGER,
  aiSearchPerMonth: Number.MAX_SAFE_INTEGER,
  cleanImagesPerDay: Number.MAX_SAFE_INTEGER,
  cleanImagesPerMonth: Number.MAX_SAFE_INTEGER,
  promotionsPerMonth: Number.MAX_SAFE_INTEGER,
  promotionDays: PLANS.expert.limits.promotionDays,
  aiRewritesPerMonth: null,
  maxImages: 30,
  video: true,
  photoScan: true,
  deepValuationsPerMonth: null,
};

/** Gränserna som gäller för en användare, med hänsyn till obegränsade konton. */
export function limitsFor(plan: PlanId, unlimited = false): PlanLimits {
  return unlimited ? UNLIMITED_LIMITS : PLANS[plan].limits;
}

export type AdUsage = { day: number; week: number; month: number };

export type QuotaCheck = {
  allowed: boolean;
  reason?: string;
  window?: "day" | "week" | "month";
};

export function checkQuota(plan: PlanId, usage: AdUsage, unlimited = false): QuotaCheck {
  if (unlimited) return { allowed: true };
  const limits = PLANS[plan].limits;


  if (limits.perDay !== null && usage.day >= limits.perDay) {
    return {
      allowed: false,
      window: "day",
      reason: `Du har nått dygnsgränsen på ${limits.perDay} annonser. Försök igen imorgon.`,
    };
  }
  if (limits.perWeek !== null && usage.week >= limits.perWeek) {
    return {
      allowed: false,
      window: "week",
      reason: `Du har nått veckogränsen på ${limits.perWeek} annonser.`,
    };
  }
  if (limits.perMonth !== null && usage.month >= limits.perMonth) {
    return {
      allowed: false,
      window: "month",
      reason:
        plan === "free"
          ? `Du har använt dina ${limits.perMonth} gratisannonser den här månaden. Uppgradera till Plus för 60 annonser i månaden.`
          : `Du har nått månadsgränsen på ${limits.perMonth} annonser. Kontakta oss om du behöver mer.`,
    };
  }
  return { allowed: true };
}

export function remainingThisMonth(plan: PlanId, usage: AdUsage): number | null {
  const perMonth = PLANS[plan].limits.perMonth;
  if (perMonth === null) return null;
  return Math.max(0, perMonth - usage.month);
}

/** Antal månader man betalar för vid årsabonnemang (två månader på köpet). */
export const YEARLY_PAID_MONTHS = 10;

/** Vad man sparar i kronor på ett år jämfört med månadsbetalning. */
export function yearlySavingSek(plan: PlanDefinition): number {
  return plan.priceSek * 12 - plan.priceYearSek;
}
